'use client'

import { createContext, useContext, useCallback, useEffect, useState } from 'react'

export type Theme = 'dark' | 'light'

interface ThemeContextValue {
  theme: Theme
  setTheme: (t: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  setTheme: () => {},
})

export function useTheme() {
  return useContext(ThemeContext)
}

function applyCookie(theme: Theme) {
  document.cookie = `gg-theme=${theme};path=/;max-age=31536000;samesite=lax`
}

function applyToDoc(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme)
}

interface ThemeProviderProps {
  initialTheme: Theme
  userEmail?: string | null
  children: React.ReactNode
}

export default function ThemeProvider({ initialTheme, userEmail, children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(initialTheme)

  // Sync to DOM whenever theme changes
  useEffect(() => {
    applyToDoc(theme)
  }, [theme])

  // On mount: if user is known, load their saved theme from KV (may override cookie)
  useEffect(() => {
    if (!userEmail) return
    fetch(`/api/user/theme?email=${encodeURIComponent(userEmail)}`)
      .then(r => r.json())
      .then(({ theme: saved }) => {
        if (saved && saved !== theme) {
          setThemeState(saved as Theme)
          applyToDoc(saved as Theme)
          applyCookie(saved as Theme)
        }
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail])

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t)
    applyToDoc(t)
    applyCookie(t)
    if (userEmail) {
      fetch('/api/user/theme', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, theme: t }),
      }).catch(() => {})
    }
  }, [userEmail])

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
